import { encode } from "html-entities";

// Tags allowed to pass through. Any tag with attributes (e.g. onclick="…")
// stays encoded because we only restore the exact bare <tag> / </tag> form.
const ALLOWED_PAIRED_TAGS = ["strong", "em", "b", "i", "u", "s", "sup", "sub", "span"];

/**
 * Sanitize CMS-authored HTML for safe use with dangerouslySetInnerHTML.
 * Encodes all markup first, then selectively restores a known-safe allowlist
 * of inline tags. Scripts, event handlers, and arbitrary attributes are
 * neutralised by the initial encode step — no DOM / jsdom required.
 */
export function sanitizeCmsHtml(html: string): string {
  let safe = encode(html);

  for (const tag of ALLOWED_PAIRED_TAGS) {
    safe = safe
      .replace(new RegExp(`&lt;${tag}&gt;`, "gi"), `<${tag}>`)
      .replace(new RegExp(`&lt;\\/${tag}&gt;`, "gi"), `</${tag}>`);
  }

  // Restore <a href="…"> — strip any javascript: or data: URLs, keep others.
  // After encode(), URL ampersands become &amp;, so ([^&]*) would truncate query strings.
  // Use a negative-lookahead pattern: capture & only when it isn't the start of &quot; or &gt;.
  safe = safe.replace(/&lt;a\s+href=&quot;((?:[^&]|&(?!quot;))*)&quot;((?:[^&]|&(?!gt;))*)&gt;/gi, (_, href, rest) => {
    // href contains &amp; for original & — that's correct HTML encoding in an attribute value.
    const safehref = /^(https?:\/\/|\/|#)/i.test(href.trim()) ? href : "#";
    const safeRest = /target/i.test(rest) ? ' target="_blank" rel="noopener noreferrer"' : "";
    return `<a href="${safehref}"${safeRest}>`;
  });
  safe = safe.replace(/&lt;\/a&gt;/gi, "</a>");

  // <br> is self-closing
  safe = safe.replace(/&lt;br\s*\/?&gt;/gi, "<br>");

  return safe;
}
